import { KeyValuePair, QuestionarySubmitDto, StoryCreatedResponse, UploadedFileModel, UploadError } from "@/models/AdditionalDataTypes";
import { QuestionairyResponse } from "@/models/QuestinaryResponse";
import { InterviewQuestionResponseModel, QuestionAnswerDto } from "@/models/QuestionModels";

export class QuestionaryService {
  private static url = '/api/InterviewQuestionary';

  static async fetchQuestions(): Promise<QuestionairyResponse> {
    const currentCulture =
        document.documentElement.dataset.culture?.toString() ?? "";
    const url = 
      this.url + 
      '?' + 
      new URLSearchParams({
        culture: currentCulture
      });

    const resp = await fetch(url, {
      headers: {
        'Content-Type': 'application/json;charset=utf-8',
        culture: currentCulture,
      },
    });

    return await resp.json();
  }

  static async postQuestionaryAnswers(answers: Array<KeyValuePair<InterviewQuestionResponseModel, string>>,isWithAdditionalQuestions: boolean = false): Promise<StoryCreatedResponse> {
    const postAnswersDtos = answers.map<QuestionAnswerDto>(pairQA => ({
      QuestionId: pairQA.key.Id,
      QuestionText: pairQA.key.Title,
      AnswerText: pairQA.value,
      AnswerType: pairQA.key.Type,
      IsAutomaticTag: pairQA.key.IsAutomaticTag,
    }))
    const dto  = <QuestionarySubmitDto>({qas: postAnswersDtos, isWithAdditionalQuestions: isWithAdditionalQuestions})

    const resp = await fetch(`${ this.url }/PostAnswers`, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'culture': document.documentElement.dataset.culture?.toString() ?? "",
      },
      body: JSON.stringify(dto),
    })

    return await resp.json()
  }

  /**
   * XMLHttpRequest rather than fetch: the Fetch API exposes no upload-progress event, and
   * TACM-1525 requires a 0-100% bar. The FormData is built exactly as before, so the request
   * on the wire is unchanged and UploadMedia needs no modification.
   *
   * onProgress reports an unrounded 0-100 float. Clamping is deliberately left to the caller,
   * which needs to distinguish "bytes sent" from "server finished".
   */
  static submitMedia(files: UploadedFileModel[], onProgress?: (percent: number) => void): Promise<any> {
      const data = new FormData()
      files.forEach((file) => {
          data.append('media', file.image, file.title)
      })

      const info = files.map((f) => ({ title: f.title, description: f.description }))
      data.append('info', JSON.stringify(info))

      return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest()
          xhr.open('POST', `${ this.url }/UploadMedia`)

          // lengthComputable is false when the browser cannot size the body. There is nothing
          // honest to report in that case, so the caller keeps whatever value it had.
          xhr.upload.onprogress = (event) => {
              if (onProgress && event.lengthComputable && event.total > 0) {
                  onProgress((event.loaded / event.total) * 100)
              }
          }

          xhr.onload = () => {
              // A 413 body is HTML from the platform edge or Kestrel, not JSON. Parsing it
              // first would surface "Unexpected token '<'" to the user.
              if (xhr.status === 413) {
                  reject(new UploadError('RequestTooLarge'))
                  return
              }

              // Guards the BadRequest(ex.Message) path too, which returns a bare JSON string
              // that the caller's files.filter(...) would throw on.
              if (xhr.status < 200 || xhr.status >= 300) {
                  reject(new UploadError('UploadFailed'))
                  return
              }

              try {
                  resolve(JSON.parse(xhr.responseText))
              } catch (_parseError) {
                  reject(new UploadError('UploadFailed'))
              }
          }

          // xhr.timeout is deliberately left unset, preserving fetch's absence of a
          // client-side timeout on this endpoint.
          xhr.onerror = () => reject(new UploadError('UploadFailed'))
          xhr.onabort = () => reject(new UploadError('UploadFailed'))

          xhr.send(data)
      })
  }
}
